/**
 * OKS-697 — Single source of truth for the mission-cover aspect ratio.
 *
 * The whole codebase framed mission covers at 16:9 (`aspect-video` /
 * `aspect-[16/9]`) until this ticket. We now crop-on-upload to a fixed
 * ratio and display at that same ratio on every surface so the framed
 * image appears pixel-identical everywhere (no `object-cover` center
 * band). The ratio lives HERE, once, so a future ratio change is a
 * single edit — retarget these two constants + the model's
 * `Mission::COVER_CARD_VARIANT` and every surface follows.
 *
 * Two shapes are exported because two consumers need different forms:
 *
 *   - `COVER_ASPECT_CLASS` — the Tailwind utility class string consumed
 *     by every display container (`className={COVER_ASPECT_CLASS}` or
 *     `cn(COVER_ASPECT_CLASS, …)`). It MUST remain a literal
 *     `"aspect-[3/2]"` string in THIS file: Tailwind v4's JIT scans
 *     source for arbitrary-value utilities, so the literal has to exist
 *     somewhere it can see it. Keeping it here (and only here) is what
 *     makes the "one edit point" guarantee real — surfaces import the
 *     symbol rather than re-typing the class, so none can silently drift
 *     back to 16:9. LQE's Vitest parity test imports this exact symbol.
 *
 *   - `COVER_ASPECT_RATIO` — the numeric ratio (width / height) consumed
 *     by `react-easy-crop`'s `aspect` prop and by the crop-area math.
 *     Kept in lockstep with the class + the server variant geometry
 *     (`Mission::COVER_CARD_VARIANT = [1200, 800]` → 1200 / 800 = 1.5).
 *
 * @see app/models/mission.rb `COVER_CARD_VARIANT`
 */

/**
 * Tailwind aspect-ratio utility for every mission-cover container.
 * Literal string (see module note) — do not build it dynamically.
 */
export const COVER_ASPECT_CLASS = "aspect-[3/2]"

/**
 * Numeric mission-cover ratio (width / height = 3 / 2 = 1.5). Feeds
 * `react-easy-crop` `aspect` and the canvas crop-area math. Mirrors the
 * server `:card` variant geometry (`[1200, 800]`).
 */
export const COVER_ASPECT_RATIO = 3 / 2
